import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'

// Components
import { Checkbox } from 'components/form/inputs'
import Distributor from 'components/interestform/formcomponents/distributor'
import DistributorNote from 'components/interestform/formcomponents/distributornote'
import Email from 'components/interestform/formcomponents/email'
import LabelArtistName from 'components/interestform/formcomponents/labelartistname'
import Links from 'components/interestform/formcomponents/links'
import Name from 'components/interestform/formcomponents/name'
import PublishingOwner from 'components/interestform/formcomponents/publishingowner'

// Types
import type { Link } from 'components/interestform/formcomponents/links/links'
import type { Distributor as DistributorType } from 'components/interestform/slides/cataloginformation/cataloginformation'

// Utils
import { pageVariants } from 'components/interestform/utils'

import commonStyles from 'components/interestform/slides/common.css'
import ContactPerson from 'components/interestform/formcomponents/contactperson'
import styles from './reviewinformation.css'

type ReviewInformationProps = {
  distributorOptions: Array<DistributorType>
  values: {
    label_artist_name: string
    links: Array<Link>
    distributor: string
    other_distributor: string
    master_rights_name: string
    master_rights_email: string
    publishing_owner: string
    publishing_rights_name: string
    publishing_rights_email: string
    first_name: string
    last_name: string
    personal_email: string
    confirmed: boolean
  }
}

const ReviewInformation = (props: ReviewInformationProps): JSX.Element => {
  const {
    distributorOptions,
    values: {
      label_artist_name,
      links,
      distributor,
      other_distributor,
      master_rights_name,
      master_rights_email,
      publishing_owner,
      publishing_rights_name,
      publishing_rights_email,
      first_name,
      last_name,
      personal_email,
      confirmed,
    },
  } = props

  const distributorData = {
    distributor,
    otherDistributor: other_distributor,
  }

  const [distributorObj, setDistributorObj] = useState<DistributorType | null>(null)

  useEffect(() => {
    const obj = distributorOptions.find(o => o.distributor_name === distributor)
    if (obj) {
      setDistributorObj(obj)
    }
  }, [distributor, distributorOptions])

  const validateConfirmed = (value: boolean) => {
    if (!value) {
      return 'You must confirm you understand before submitting your information'
    }
    return ''
  }

  return (
    <motion.div className={commonStyles.wrapper} initial="initial" animate="in" exit="out" variants={pageVariants}>
      <h1 className={styles.heading}>Review & Submit</h1>
      <div className={styles.subheading}>
        Take a few moments to look through the information you&apos;ve provided. Check everything is correct, and then get it sent over to
        us for review!
      </div>
      <div>
        <div className={styles.questionWrapper}>
          <div className={styles.questionTitle}>What is your Label or Artist name?</div>
          <LabelArtistName onReview labelArtistName={label_artist_name} />
        </div>
        <div className={styles.questionWrapper}>
          <div className={styles.questionTitle}>Where can we review your music?</div>
          <Links onReview links={links} />
        </div>
        <div className={styles.questionWrapper}>
          <div className={styles.questionTitle}>Who is your Distributor?</div>
          <Distributor onReview distributorData={distributorData} distributorOptions={distributorOptions} />
        </div>

        <div className={styles.questionWrapper}>
          <div className={styles.questionTitle}>Master rights contact</div>
          <ContactPerson onReview typeOfRights="master" name={master_rights_name} email={master_rights_email} />
        </div>
        <div className={styles.questionWrapper}>
          <div className={styles.questionTitle}>How are the publishing rights for your music handled?</div>
          <PublishingOwner onReview publishingOwner={publishing_owner} />
        </div>
        <div className={styles.questionWrapper}>
          <div className={styles.questionTitle}>Publishing rights contact</div>
          <ContactPerson onReview typeOfRights="publishing" name={publishing_rights_name} email={publishing_rights_email} />
        </div>
        <div className={styles.questionWrapper}>
          <div className={styles.questionTitle}>What is your name?</div>
          <Name onReview firstName={first_name} lastName={last_name} />
        </div>
        <div className={styles.questionWrapper}>
          <div className={styles.questionTitle}>Email to contact you directly</div>
          <Email onReview emailKey="personal" email={personal_email} />
        </div>

        <div className={styles.confirmationWrapper}>
          <div className={styles.questionTitle}>Do you understand what opting into Tracklib means?</div>
          <div className={styles.questionDetails}>
            Submitting your information <strong>does not</strong> yet bind you to any kind of contract with <strong>Tracklib</strong>. We
            will review your music and then you will receive a contract to sign through the &quot;HelloSign&quot; platform. We will then
            contact your distributor to discuss how to proceed
          </div>
          {distributorObj && distributorObj.distributor_note !== '' && <DistributorNote note={distributorObj.distributor_note} />}
          <Checkbox className={styles.confirmation} name="confirmed" size="large" bordered value={confirmed} validate={validateConfirmed}>
            <span className={styles.confirmationText}>Yes, I understand and I wish to put my content on Tracklib</span>
          </Checkbox>
        </div>
      </div>
    </motion.div>
  )
}

export default ReviewInformation
